import React from "react";
import {
  Building2,
  CalendarDays,
  Database,
  LineChart,
  Search,
  Sparkles,
  Users,
} from "lucide-react";
import Modal from "./Modal";
import "./proFeaturesModal.css";
import type { BillingPlanId, PlanTier } from "../../types/billing";

interface ProFeaturesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: (planId: BillingPlanId) => Promise<boolean>;
  disabled?: boolean;
  loading?: boolean;
  currentPlanTier?: PlanTier;
  pricesConfigured?: Record<BillingPlanId, boolean>;
  proPriceLabel?: string;
  agencyPriceLabel?: string;
}

const PRO_FEATURES = [
  {
    title: "3 brands",
    description: "Run a small brand workspace with scheduled publishing.",
    icon: Building2,
  },
  {
    title: "300 monthly generations",
    description: "Enough capacity for regular first-draft production.",
    icon: Sparkles,
  },
  {
    title: "10 knowledge sources",
    description: "Add websites, documents, and core brand context.",
    icon: Database,
  },
  {
    title: "Post scheduling",
    description: "Plan content ahead and publish at the right time.",
    icon: CalendarDays,
  },
  {
    title: "SEO opportunities",
    description: "Find high-impact search content ideas for your brand.",
    icon: Search,
  },
  {
    title: "Social signal listening",
    description: "Track competitor and audience signals in one place.",
    icon: LineChart,
  },
  {
    title: "Lead generation",
    description: "Unlock growth workflows built for outbound prospecting.",
    icon: Users,
  },
];

const AGENCY_FEATURES = [
  {
    title: "10 client brands",
    description: "Manage multiple client workspaces from one account.",
    icon: Building2,
  },
  {
    title: "1,000 monthly generations",
    description: "Support weekly social production across client brands.",
    icon: Sparkles,
  },
  {
    title: "50 knowledge sources",
    description: "Store deeper client context across websites and documents.",
    icon: Database,
  },
  {
    title: "Priority support",
    description: "Get faster help for agency production workflows.",
    icon: Users,
  },
];

const ProFeaturesModal: React.FC<ProFeaturesModalProps> = ({
  isOpen,
  onClose,
  onUpgrade,
  disabled = false,
  loading = false,
  currentPlanTier = "free",
  pricesConfigured = { pro_monthly: true, agency_monthly: true },
  proPriceLabel = "...",
  agencyPriceLabel = "...",
}) => {
  const hasPro = currentPlanTier === "pro" || currentPlanTier === "agency";
  const hasAgency = currentPlanTier === "agency";
  const canStartPro = pricesConfigured.pro_monthly && !hasPro;
  const canStartAgency = pricesConfigured.agency_monthly && !hasAgency;
  const proPriceIsAmount = /^[$₹]/.test(proPriceLabel);
  const agencyPriceIsAmount = /^[$₹]/.test(agencyPriceLabel);
  const footer = (
    <>
      <button
        type="button"
        className="pro-features-modal__secondary-btn"
        onClick={onClose}
      >
        Maybe later
      </button>
      <div className="pro-features-modal__footer-actions">
        <button
          type="button"
          className="pro-features-modal__secondary-upgrade-btn"
          onClick={() => {
            void onUpgrade("pro_monthly");
          }}
          disabled={disabled || loading || !canStartPro}
        >
          {hasPro
            ? "Current plan covers Pro"
            : !pricesConfigured.pro_monthly
              ? "Pro checkout unavailable"
              : loading
                ? "Opening..."
                : "Start Pro"}
        </button>
        <button
          type="button"
          className="pro-features-modal__primary-btn"
          onClick={() => {
            void onUpgrade("agency_monthly");
          }}
          disabled={disabled || loading || !canStartAgency}
        >
          {hasAgency
            ? "Current plan"
            : !pricesConfigured.agency_monthly
              ? "Agency checkout unavailable"
              : loading
                ? "Opening checkout..."
                : "Start Agency"}
        </button>
      </div>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Upgrade Impret"
      size="large"
      className="pro-features-modal"
      bodyStyle={{ padding: 0 }}
      footer={footer}
      footerStyle={{
        padding: "20px 24px",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <div className="pro-features-modal__content">
        <section className="pro-features-modal__hero">
          <div className="pro-features-modal__eyebrow">Impret Plans</div>
          <h3 className="pro-features-modal__headline">
            Choose the workspace size that fits your content volume
          </h3>
          <p className="pro-features-modal__summary">
            Pro is for one operator managing a few brands. Agency is for
            recurring client content production across multiple brands.
          </p>
          <div className="pro-features-modal__pricing">
            <span className="pro-features-modal__price">{proPriceLabel}</span>
            <span className="pro-features-modal__price-unit">
              {proPriceIsAmount ? "/ month Pro" : "Pro"}
            </span>
            <span className="pro-features-modal__price-divider">or</span>
            <span className="pro-features-modal__price">{agencyPriceLabel}</span>
            <span className="pro-features-modal__price-unit">
              {agencyPriceIsAmount ? "/ month Agency" : "Agency"}
            </span>
          </div>
        </section>

        <section className="pro-features-modal__limits">
          <div className="pro-features-modal__plan-card pro-features-modal__plan-card--free">
            <div className="pro-features-modal__plan-name">Free</div>
            <div className="pro-features-modal__plan-copy">
              1 brand, 50 monthly generations, 1 knowledge source
            </div>
          </div>
          <div className="pro-features-modal__plan-card pro-features-modal__plan-card--pro">
            <div className="pro-features-modal__plan-name">Pro</div>
            <div className="pro-features-modal__plan-copy">
              3 brands, 300 monthly generations, 10 knowledge sources
            </div>
          </div>
          <div className="pro-features-modal__plan-card pro-features-modal__plan-card--pro">
            <div className="pro-features-modal__plan-name">Agency</div>
            <div className="pro-features-modal__plan-copy">
              10 brands, 1,000 monthly generations, 50 knowledge sources
            </div>
          </div>
        </section>

        <section className="pro-features-modal__features">
          <div className="pro-features-modal__section-title">
            Pro includes
          </div>
          <div className="pro-features-modal__feature-grid">
            {PRO_FEATURES.map((feature) => {
              const Icon = feature.icon;
              return (
                <article
                  key={feature.title}
                  className="pro-features-modal__feature-card"
                >
                  <div className="pro-features-modal__feature-icon">
                    <Icon size={18} />
                  </div>
                  <div>
                    <div className="pro-features-modal__feature-title">
                      {feature.title}
                    </div>
                    <p className="pro-features-modal__feature-description">
                      {feature.description}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className="pro-features-modal__features">
          <div className="pro-features-modal__section-title">
            Agency adds
          </div>
          <div className="pro-features-modal__feature-grid">
            {AGENCY_FEATURES.map((feature) => {
              const Icon = feature.icon;
              return (
                <article
                  key={feature.title}
                  className="pro-features-modal__feature-card"
                >
                  <div className="pro-features-modal__feature-icon">
                    <Icon size={18} />
                  </div>
                  <div>
                    <div className="pro-features-modal__feature-title">
                      {feature.title}
                    </div>
                    <p className="pro-features-modal__feature-description">
                      {feature.description}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      </div>
    </Modal>
  );
};

export default ProFeaturesModal;
